import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Input,
  Select,
  HStack,
  VStack,
  Spinner,
  Center,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
  IconButton,
  Tooltip,
  Textarea,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  Grid,
  GridItem,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tag,
  TagLabel,
  TagLeftIcon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Divider,
  Switch,
  FormHelperText,
  Checkbox,
} from '@chakra-ui/react';
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiEye, 
  FiSettings,
  FiDollarSign,
  FiClock,
  FiTag
} from 'react-icons/fi';
import { 
  getAllServiceTypes, 
  ServiceType, 
  ServiceVariant, 
  ServiceExtra,
  createServiceType,
  updateServiceType,
  deleteServiceType
} from '../services/firestore';
import ExportModal from './Exports/ExportModal';

const ServicesManagement: React.FC = () => {
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [filteredServiceTypes, setFilteredServiceTypes] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedServiceType, setSelectedServiceType] = useState<ServiceType | null>(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [priceAdjustPct, setPriceAdjustPct] = useState('');
  const [isExportOpen, setIsExportOpen] = useState(false);
  
  // Create/Edit form state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    category: '',
    basePrice: '',
    isActive: true,
    sortOrder: 0,
    imageUrl: '',
    variants: [] as ServiceVariant[],
    extras: [] as ServiceExtra[],
    features: [] as string[]
  });

  const toast = useToast();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchServiceTypes();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    filterServiceTypes();
  }, [serviceTypes, searchTerm, categoryFilter]);

  const fetchServiceTypes = async () => {
    try {
      setLoading(true);
      const data = await getAllServiceTypes();
      setServiceTypes(data);
    } catch (error) {
      console.error('Error fetching service types:', error);
      toast({
        title: 'Error loading services',
        description: 'Failed to fetch service types',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterServiceTypes = () => {
    let filtered = serviceTypes;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(service =>
        service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(service => service.category === categoryFilter);
    }

    setFilteredServiceTypes(filtered);
  };

  const handleCreateService = async () => {
    try {
      setCreating(true);
      
      // Validate required fields
      if (!serviceForm.name || !serviceForm.category) {
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const newServiceType = await createServiceType({
        name: serviceForm.name,
        description: serviceForm.description,
        category: serviceForm.category,
        basePrice: serviceForm.basePrice ? parseFloat(serviceForm.basePrice) : undefined,
        isActive: serviceForm.isActive,
        sortOrder: serviceForm.sortOrder,
        imageUrl: serviceForm.imageUrl,
        variants: serviceForm.variants,
        extras: serviceForm.extras,
        features: serviceForm.features
      });

      // Add to local state
      setServiceTypes([...serviceTypes, newServiceType]);

      // Reset form
      resetServiceForm();
      setIsCreateOpen(false);

      toast({
        title: 'Service created',
        description: 'New service has been created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error creating service:', error);
      toast({
        title: 'Creation failed',
        description: 'Failed to create service',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  const handleEditService = (service: ServiceType) => {
    setSelectedServiceType(service);
    setServiceForm({
      name: service.name,
      description: service.description || '',
      category: service.category,
      basePrice: service.basePrice?.toString() || '',
      isActive: service.isActive,
      sortOrder: service.sortOrder,
      imageUrl: service.imageUrl || '',
      variants: service.variants,
      extras: service.extras,
      features: service.features || []
    });
    setIsEditOpen(true);
  };

  const handleUpdateService = async () => {
    if (!selectedServiceType) return;

    try {
      setUpdating(true);
      
      await updateServiceType(selectedServiceType.id, {
        name: serviceForm.name,
        description: serviceForm.description,
        category: serviceForm.category,
        basePrice: serviceForm.basePrice ? parseFloat(serviceForm.basePrice) : undefined,
        isActive: serviceForm.isActive,
        sortOrder: serviceForm.sortOrder,
        imageUrl: serviceForm.imageUrl,
        variants: serviceForm.variants,
        extras: serviceForm.extras,
        features: serviceForm.features
      });

      // Update local state
      setServiceTypes(serviceTypes.map(service => 
        service.id === selectedServiceType.id 
          ? { ...service, ...serviceForm, basePrice: serviceForm.basePrice ? parseFloat(serviceForm.basePrice) : undefined }
          : service
      ));

      resetServiceForm();
      setIsEditOpen(false);

      toast({
        title: 'Service updated',
        description: 'Service has been updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating service:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update service',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteService = async (serviceId: string) => {
    if (!window.confirm('Are you sure you want to delete this service? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      
      await deleteServiceType(serviceId);

      // Remove from local state
      setServiceTypes(serviceTypes.filter(service => service.id !== serviceId));

      toast({
        title: 'Service deleted',
        description: 'Service has been deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting service:', error);
      toast({
        title: 'Deletion failed',
        description: 'Failed to delete service',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  const resetServiceForm = () => {
    setServiceForm({
      name: '',
      description: '',
      category: '',
      basePrice: '',
      isActive: true,
      sortOrder: 0,
      imageUrl: '',
      variants: [],
      extras: [],
      features: []
    });
  };

  const handleFormChange = (field: string, value: any) => {
    setServiceForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addVariant = () => {
    const newVariant: ServiceVariant = {
      id: `variant_${Date.now()}`,
      name: '',
      description: '',
      price: 0,
      duration: 0,
      isActive: true
    };
    setServiceForm(prev => ({
      ...prev,
      variants: [...prev.variants, newVariant]
    }));
  };

  const updateVariant = (index: number, field: string, value: any) => {
    setServiceForm(prev => ({
      ...prev,
      variants: prev.variants.map((variant, i) => 
        i === index ? { ...variant, [field]: value } : variant
      )
    }));
  };

  const removeVariant = (index: number) => {
    setServiceForm(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const addExtra = () => {
    const newExtra: ServiceExtra = {
      id: `extra_${Date.now()}`,
      name: '',
      description: '',
      price: 0,
      duration: 0,
      isActive: true,
      category: ''
    };
    setServiceForm(prev => ({
      ...prev,
      extras: [...prev.extras, newExtra]
    }));
  };

  const updateExtra = (index: number, field: string, value: any) => {
    setServiceForm(prev => ({
      ...prev,
      extras: prev.extras.map((extra, i) => 
        i === index ? { ...extra, [field]: value } : extra
      )
    }));
  };

  const removeExtra = (index: number) => {
    setServiceForm(prev => ({
      ...prev,
      extras: prev.extras.filter((_, i) => i !== index)
    }));
  };

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'exterior':
        return 'blue';
      case 'interior':
        return 'purple';
      case 'special':
        return 'green';
      case 'premium':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const getServiceStats = () => {
    const stats = {
      total: serviceTypes.length,
      active: serviceTypes.filter(s => s.isActive).length,
      categories: new Set(serviceTypes.map(s => s.category)).size,
      totalVariants: serviceTypes.reduce((sum, s) => sum + s.variants.length, 0),
      totalExtras: serviceTypes.reduce((sum, s) => sum + s.extras.length, 0)
    };
    return stats;
  };

  const serviceStats = getServiceStats();
  const categories = Array.from(new Set(serviceTypes.map(s => s.category)));

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
        <Heading 
          size={{ base: 'md', md: 'lg' }}
          pl={{ base: '60px', lg: 0 }}
          pr={{ base: 4, lg: 0 }}
          flex="1"
          minW="200px"
        >
          Services Management
        </Heading>
        <HStack>
          {selectedServiceIds.length > 0 && (
            <HStack>
              <Button size="sm" colorScheme="green" minH="44px" onClick={async()=>{
                setBulkLoading(true);
                try {
                  await Promise.all(selectedServiceIds.map(id => updateServiceType(id, { isActive: true })));
                  setServiceTypes(serviceTypes.map(s => selectedServiceIds.includes(s.id) ? { ...s, isActive: true } : s));
                  setSelectedServiceIds([]);
                  toast({ title: 'Services enabled', status: 'success', duration: 2000, isClosable: true });
                } finally { setBulkLoading(false); }
              }} isLoading={bulkLoading}>Enable</Button>
              <Button size="sm" colorScheme="red" variant="outline" minH="44px" onClick={async()=>{
                setBulkLoading(true);
                try {
                  await Promise.all(selectedServiceIds.map(id => updateServiceType(id, { isActive: false })));
                  setServiceTypes(serviceTypes.map(s => selectedServiceIds.includes(s.id) ? { ...s, isActive: false } : s));
                  setSelectedServiceIds([]);
                  toast({ title: 'Services disabled', status: 'success', duration: 2000, isClosable: true });
                } finally { setBulkLoading(false); }
              }} isLoading={bulkLoading}>Disable</Button>
              <HStack>
                <NumberInput size="sm" value={priceAdjustPct} onChange={(_, v)=> setPriceAdjustPct(Number.isFinite(v)? String(v): '')} w="140px">
                  <NumberInputField placeholder="% adjust" />
                </NumberInput>
                <Button size="sm" minH="44px" onClick={async()=>{
                  const pct = parseFloat(priceAdjustPct);
                  if (!Number.isFinite(pct)) return;
                  setBulkLoading(true);
                  try {
                    await Promise.all(selectedServiceIds.map(id => {
                      const svc = serviceTypes.find(s => s.id === id);
                      const base = svc?.basePrice ?? 0;
                      const newBase = base ? parseFloat((base * (1 + pct/100)).toFixed(2)) : undefined;
                      return updateServiceType(id, { basePrice: newBase });
                    }));
                    setServiceTypes(serviceTypes.map(s => selectedServiceIds.includes(s.id) ? { ...s, basePrice: s.basePrice ? parseFloat((s.basePrice * (1 + parseFloat(priceAdjustPct)/100)).toFixed(2)) : s.basePrice } : s));
                    setSelectedServiceIds([]); setPriceAdjustPct('');
                    toast({ title: 'Prices adjusted', status: 'success', duration: 2000, isClosable: true });
                  } finally { setBulkLoading(false); }
                }} isLoading={bulkLoading}>Apply %</Button>
              </HStack>
            </HStack>
          )}
          <Button size="sm" variant="outline" minH="44px" onClick={()=> setIsExportOpen(true)}>Export</Button>
          <Button 
          leftIcon={React.createElement(FiPlus as React.ElementType)} 
          colorScheme="blue"
          onClick={() => setIsCreateOpen(true)}
        >
          Add Service
        </Button>
        </HStack>
      </Flex>

      {/* Service Statistics */}
      <Grid templateColumns="repeat(5, 1fr)" gap={4} mb={6}>
        <GridItem>
          <Stat>
            <StatLabel>Total Services</StatLabel>
            <StatNumber color="blue.500">{serviceStats.total}</StatNumber>
          </Stat>
        </GridItem>
        <GridItem>
          <Stat>
            <StatLabel>Active Services</StatLabel>
            <StatNumber color="green.500">{serviceStats.active}</StatNumber>
          </Stat>
        </GridItem>
        <GridItem>
          <Stat>
            <StatLabel>Categories</StatLabel>
            <StatNumber color="purple.500">{serviceStats.categories}</StatNumber>
          </Stat>
        </GridItem>
        <GridItem>
          <Stat>
            <StatLabel>Total Variants</StatLabel>
            <StatNumber color="orange.500">{serviceStats.totalVariants}</StatNumber>
          </Stat>
        </GridItem>
        <GridItem>
          <Stat>
            <StatLabel>Total Extras</StatLabel>
            <StatNumber color="teal.500">{serviceStats.totalExtras}</StatNumber>
          </Stat>
        </GridItem>
      </Grid>

      {/* Search and Filter Controls */}
      <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={6}>
        <HStack spacing={4}>
          <Box flex={1}>
            <FormLabel fontSize="sm">Search Services</FormLabel>
            <Input
              placeholder="Search by name, description, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>
          <Box minW="200px">
            <FormLabel fontSize="sm">Filter by Category</FormLabel>
            <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </Select>
          </Box>
        </HStack>
      </Box>

      {/* Services Table */}
      <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
        <Box 
          overflowX="auto" 
          overflowY="auto"
          maxH="calc(100vh - 400px)"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
        >
          <Table variant="simple" minW="100%">
          <Thead>
            <Tr>
              <Th>
                <Checkbox
                  isChecked={selectedServiceIds.length === filteredServiceTypes.length && filteredServiceTypes.length > 0}
                  isIndeterminate={selectedServiceIds.length > 0 && selectedServiceIds.length < filteredServiceTypes.length}
                  onChange={(e: any)=> setSelectedServiceIds(e.target.checked ? filteredServiceTypes.map(s=>s.id) : [])}
                />
              </Th>
              <Th>Service Name</Th>
              <Th>Category</Th>
              <Th>Base Price</Th>
              <Th>Variants</Th>
              <Th>Extras</Th>
              <Th>Status</Th>
              <Th>Sort Order</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredServiceTypes.map((service) => (
              <Tr key={service.id}>
                <Td>
                  <Checkbox
                    isChecked={selectedServiceIds.includes(service.id)}
                    onChange={(e: any)=> setSelectedServiceIds(e.target.checked ? [...selectedServiceIds, service.id] : selectedServiceIds.filter(id=>id!==service.id))}
                  />
                </Td>
                <Td>
                  <Box>
                    <Text fontWeight="bold">{service.name}</Text>
                    {service.description && (
                      <Text fontSize="sm" color="gray.600" noOfLines={2}>
                        {service.description}
                      </Text>
                    )}
                  </Box>
                </Td>
                <Td>
                  <Tag colorScheme={getCategoryColor(service.category)} size="sm">
                    {service.category}
                  </Tag>
                </Td>
                <Td>
                  {service.basePrice ? `$${service.basePrice.toFixed(2)}` : 'N/A'}
                </Td>
                <Td>
                  <HStack spacing={1}>
                    <Text fontSize="sm">{service.variants.length}</Text>
                    {service.variants.length > 0 && (
                      <Tooltip label={service.variants.map(v => `${v.name}: $${v.price}`).join(', ')}>
                        <IconButton
                          size="sm"
                          icon={React.createElement(FiEye as React.ElementType)}
                          aria-label="View variants"
                          variant="ghost"
                          minH="44px"
                          minW="44px"
                        />
                      </Tooltip>
                    )}
                  </HStack>
                </Td>
                <Td>
                  <HStack spacing={1}>
                    <Text fontSize="sm">{service.extras.length}</Text>
                    {service.extras.length > 0 && (
                      <Tooltip label={service.extras.map(e => `${e.name}: $${e.price}`).join(', ')}>
                        <IconButton
                          size="sm"
                          icon={React.createElement(FiEye as React.ElementType)}
                          aria-label="View extras"
                          variant="ghost"
                          minH="44px"
                          minW="44px"
                        />
                      </Tooltip>
                    )}
                  </HStack>
                </Td>
                <Td>
                  <Badge colorScheme={service.isActive ? 'green' : 'red'}>
                    {service.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </Td>
                <Td>{service.sortOrder}</Td>
                <Td>
                  <HStack spacing={2}>
                    <Tooltip label="Edit Service">
                      <IconButton
                        size="sm"
                        icon={React.createElement(FiEdit2 as React.ElementType)}
                        aria-label="Edit service"
                        variant="ghost"
                        onClick={() => handleEditService(service)}
                        minH="44px"
                        minW="44px"
                      />
                    </Tooltip>
                    <Tooltip label="Delete Service">
                      <IconButton
                        size="sm"
                        icon={React.createElement(FiTrash2 as React.ElementType)}
                        aria-label="Delete service"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDeleteService(service.id)}
                        isLoading={deleting}
                        minH="44px"
                        minW="44px"
                      />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        </Box>

        {filteredServiceTypes.length === 0 && (
          <Center py={8}>
            <Text color="gray.500">No services found</Text>
          </Center>
        )}
      </Box>

      {/* Create Service Modal */}
      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Service</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ServiceForm 
              form={serviceForm}
              onFormChange={handleFormChange}
              onAddVariant={addVariant}
              onUpdateVariant={updateVariant}
              onRemoveVariant={removeVariant}
              onAddExtra={addExtra}
              onUpdateExtra={updateExtra}
              onRemoveExtra={removeExtra}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleCreateService}
              isLoading={creating}
              loadingText="Creating"
            >
              Create Service
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Service Modal */}
      <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Service</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ServiceForm 
              form={serviceForm}
              onFormChange={handleFormChange}
              onAddVariant={addVariant}
              onUpdateVariant={updateVariant}
              onRemoveVariant={removeVariant}
              onAddExtra={addExtra}
              onUpdateExtra={updateExtra}
              onRemoveExtra={removeExtra}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsEditOpen(false)}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleUpdateService}
              isLoading={updating}
              loadingText="Updating"
            >
              Update Service
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Export Modal */}
      <ExportModal
        isOpen={isExportOpen}
        onClose={()=> setIsExportOpen(false)}
        data={filteredServiceTypes as any}
        title="Export Services"
        defaultFilename="services_export"
      />
    </Box>
  );
};

// Service Form Component
interface ServiceFormProps {
  form: any;
  onFormChange: (field: string, value: any) => void;
  onAddVariant: () => void;
  onUpdateVariant: (index: number, field: string, value: any) => void;
  onRemoveVariant: (index: number) => void;
  onAddExtra: () => void;
  onUpdateExtra: (index: number, field: string, value: any) => void;
  onRemoveExtra: (index: number) => void;
}

const ServiceForm: React.FC<ServiceFormProps> = ({
  form,
  onFormChange,
  onAddVariant,
  onUpdateVariant,
  onRemoveVariant,
  onAddExtra,
  onUpdateExtra,
  onRemoveExtra
}) => {
  return (
    <VStack spacing={6} align="stretch">
      {/* Basic Information */}
      <Box>
        <Heading size="md" mb={4}>Basic Information</Heading>
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <FormControl isRequired>
            <FormLabel>Service Name</FormLabel>
            <Input
              value={form.name}
              onChange={(e) => onFormChange('name', e.target.value)}
              placeholder="e.g., eXpress Wash"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Category</FormLabel>
            <Select
              value={form.category}
              onChange={(e) => onFormChange('category', e.target.value)}
              placeholder="Select category"
            >
              <option value="exterior">Exterior</option>
              <option value="interior">Interior</option>
              <option value="special">Special</option>
              <option value="premium">Premium</option>
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Base Price ($)</FormLabel>
            <NumberInput
              value={form.basePrice}
              onChange={(_, value) => onFormChange('basePrice', value.toString())}
              min={0}
              precision={2}
            >
              <NumberInputField placeholder="0.00" />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <FormHelperText>Leave empty if using variants</FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>Sort Order</FormLabel>
            <NumberInput
              value={form.sortOrder}
              onChange={(_, value) => onFormChange('sortOrder', value)}
              min={0}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
        </Grid>

        <FormControl mt={4}>
          <FormLabel>Description</FormLabel>
          <Textarea
            value={form.description}
            onChange={(e) => onFormChange('description', e.target.value)}
            placeholder="Describe the service..."
            rows={3}
          />
        </FormControl>

        <FormControl mt={4}>
          <FormLabel>Features (one per line)</FormLabel>
          <Textarea
            value={form.features?.join('\n') || ''}
            onChange={(e) => {
              const features = e.target.value.split('\n').filter(f => f.trim() !== '');
              onFormChange('features', features);
            }}
            placeholder="Enter features, one per line&#10;e.g.,&#10;Hand wash&#10;Tire shine&#10;Wax"
            rows={4}
          />
          <FormHelperText>Enter service features, one per line</FormHelperText>
        </FormControl>

        <FormControl mt={4}>
          <FormLabel>Image URL</FormLabel>
          <Input
            value={form.imageUrl}
            onChange={(e) => onFormChange('imageUrl', e.target.value)}
            placeholder="https://example.com/image.jpg"
          />
        </FormControl>

        <FormControl mt={4}>
          <FormLabel>Active Status</FormLabel>
          <Switch
            isChecked={form.isActive}
            onChange={(e) => onFormChange('isActive', e.target.checked)}
          />
        </FormControl>
      </Box>

      <Divider />

      {/* Variants */}
      <Box>
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size="md">Variants</Heading>
          <Button size="sm" leftIcon={React.createElement(FiPlus as React.ElementType)} minH="44px" onClick={onAddVariant}>
            Add Variant
          </Button>
        </Flex>
        
        {form.variants.length === 0 ? (
          <Alert status="info">
            <AlertIcon />
            <Box>
              <AlertTitle>No Variants</AlertTitle>
              <AlertDescription>
                Add variants for different vehicle types or service levels (e.g., Car vs SUV)
              </AlertDescription>
            </Box>
          </Alert>
        ) : (
          <VStack spacing={4} align="stretch">
            {form.variants.map((variant: ServiceVariant, index: number) => (
              <Box key={variant.id} p={4} border="1px" borderColor="gray.200" borderRadius="md">
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontWeight="bold">Variant {index + 1}</Text>
                  <IconButton
                    size="sm"
                    icon={React.createElement(FiTrash2 as React.ElementType)}
                    aria-label="Remove variant"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => onRemoveVariant(index)}
                  />
                </Flex>
                
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <FormControl>
                    <FormLabel>Name</FormLabel>
                    <Input
                      value={variant.name}
                      onChange={(e) => onUpdateVariant(index, 'name', e.target.value)}
                      placeholder="e.g., Car, SUV, Truck"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Price ($)</FormLabel>
                    <NumberInput
                      value={variant.price}
                      onChange={(_, value) => onUpdateVariant(index, 'price', value)}
                      min={0}
                      precision={2}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Duration (minutes)</FormLabel>
                    <NumberInput
                      value={variant.duration}
                      onChange={(_, value) => onUpdateVariant(index, 'duration', value)}
                      min={0}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Active</FormLabel>
                    <Switch
                      isChecked={variant.isActive}
                      onChange={(e) => onUpdateVariant(index, 'isActive', e.target.checked)}
                    />
                  </FormControl>
                </Grid>

                <FormControl mt={3}>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={variant.description}
                    onChange={(e) => onUpdateVariant(index, 'description', e.target.value)}
                    placeholder="Describe this variant..."
                    rows={2}
                  />
                </FormControl>
              </Box>
            ))}
          </VStack>
        )}
      </Box>

      <Divider />

      {/* Extras */}
      <Box>
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size="md">Extras</Heading>
          <Button size="sm" leftIcon={React.createElement(FiPlus as React.ElementType)} minH="44px" onClick={onAddExtra}>
            Add Extra
          </Button>
        </Flex>
        
        {form.extras.length === 0 ? (
          <Alert status="info">
            <AlertIcon />
            <Box>
              <AlertTitle>No Extras</AlertTitle>
              <AlertDescription>
                Add extras that customers can select (e.g., Interior Detail, Wax, etc.)
              </AlertDescription>
            </Box>
          </Alert>
        ) : (
          <VStack spacing={4} align="stretch">
            {form.extras.map((extra: ServiceExtra, index: number) => (
              <Box key={extra.id} p={4} border="1px" borderColor="gray.200" borderRadius="md">
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontWeight="bold">Extra {index + 1}</Text>
                  <IconButton
                    size="sm"
                    icon={React.createElement(FiTrash2 as React.ElementType)}
                    aria-label="Remove extra"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => onRemoveExtra(index)}
                  />
                </Flex>
                
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <FormControl>
                    <FormLabel>Name</FormLabel>
                    <Input
                      value={extra.name}
                      onChange={(e) => onUpdateExtra(index, 'name', e.target.value)}
                      placeholder="e.g., Interior Detail"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Category</FormLabel>
                    <Select
                      value={extra.category}
                      onChange={(e) => onUpdateExtra(index, 'category', e.target.value)}
                      placeholder="Select category"
                    >
                      <option value="interior">Interior</option>
                      <option value="exterior">Exterior</option>
                      <option value="premium">Premium</option>
                      <option value="protection">Protection</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Price ($)</FormLabel>
                    <NumberInput
                      value={extra.price}
                      onChange={(_, value) => onUpdateExtra(index, 'price', value)}
                      min={0}
                      precision={2}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Additional Time (minutes)</FormLabel>
                    <NumberInput
                      value={extra.duration}
                      onChange={(_, value) => onUpdateExtra(index, 'duration', value)}
                      min={0}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Active</FormLabel>
                    <Switch
                      isChecked={extra.isActive}
                      onChange={(e) => onUpdateExtra(index, 'isActive', e.target.checked)}
                    />
                  </FormControl>
                </Grid>

                <FormControl mt={3}>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={extra.description}
                    onChange={(e) => onUpdateExtra(index, 'description', e.target.value)}
                    placeholder="Describe this extra service..."
                    rows={2}
                  />
                </FormControl>
              </Box>
            ))}
          </VStack>
        )}
      </Box>
    </VStack>
  );
};

export default ServicesManagement; 